import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Pencil, 
  Play, 
  Settings, 
  Tag, 
  User, 
  Eye,
  Code,
  Database,
  Layers,
  Users,
  Copy,
  CheckCircle,
  Bot,
  Wrench,
  FileText,
  Zap,
  Shield,
  Globe
} from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { usePlaybooksApi } from '../apis/playbooks-apis';
import { useUsersApi } from '../apis/users-apis';
import { Playbook } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { TriggerModal } from '../components/TriggerModal';

const ViewPlaybookPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const playbooksApi = usePlaybooksApi();
  const usersApi = useUsersApi();
  const { user, isAdmin } = useAuth();
  
  const [playbook, setPlaybook] = useState<Playbook | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [ownerEmail, setOwnerEmail] = useState<string | null>(null);
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);

  useEffect(() => {
    const fetchPlaybook = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const fetchedPlaybook = await playbooksApi.getPlaybook(id);
        
        if (fetchedPlaybook) {
          setPlaybook(fetchedPlaybook);
          // Fetch owner email if different from current user
          try {
            if (fetchedPlaybook.ownerId && fetchedPlaybook.ownerId !== user?.sub) {
              const ownerData = await usersApi.getUser(fetchedPlaybook.ownerId);
              setOwnerEmail(ownerData.email);
            } else {
              setOwnerEmail(user?.email || null);
            }
          } catch {
            setOwnerEmail(null);
          }
        } else {
          addToast({
            title: "Error",
            description: "Playbook not found",
            variant: "destructive",
          });
          navigate('/studio/playbooks');
        }
      } catch (error) {
        console.error('Error fetching playbook:', error);
        addToast({
          title: "Error",
          description: "Failed to load playbook details",
          variant: "destructive",
        });
        navigate('/studio/playbooks');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlaybook();
  }, [id, user?.sub, user?.email]); // Only depend on the specific values that should trigger refetch

  const handleCopyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
      addToast({
        title: "Copied",
        description: `${fieldName} copied to clipboard`,
        variant: "default",
      });
    } catch (error) {
      addToast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleTriggerSubmit = async (playbookId: string, inputData: string) => {
    await playbooksApi.triggerPlaybook(playbookId, inputData);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Spinner className="w-8 h-8 text-sky-500" />
        <p className="text-gray-600">Loading playbook details...</p>
      </div>
    );
  }

  if (!playbook) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="bg-gradient-to-br from-red-100 to-red-200 p-4 rounded-full">
          <Eye className="h-8 w-8 text-red-600" />
        </div>
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Playbook Not Found</h3>
          <p className="text-gray-600 mb-4">The requested playbook could not be found.</p>
          <Button onClick={() => navigate('/studio/playbooks')}>
            Back to Playbooks
          </Button>
        </div>
      </div>
    );
  }

  const isOwned = playbook.ownerId === user?.sub;
  const canEdit = isOwned || isAdmin;

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-6xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/studio/playbooks')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 sm:px-3 py-2 rounded-lg transition-colors text-sm sm:text-base"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden xs:inline">Back to Playbooks</span>
              <span className="xs:hidden">Back</span>
            </Button>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <Button
              onClick={() => setTriggerModalOpen(true)}
              className="group relative bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base"
            >
              <div className="flex items-center gap-2 justify-center">
                <div className="bg-white/20 p-1 rounded-lg group-hover:bg-white/30 transition-colors">
                  <Play className="h-3 w-3 sm:h-4 sm:w-4" />
                </div>
                <span>Trigger Playbook</span>
              </div>
            </Button>
            
            {canEdit && (
              <Button
                onClick={() => navigate(`/studio/playbooks/edit/${playbook.playbookId}`)}
                className="group relative bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base"
              >
                <div className="flex items-center gap-2 justify-center">
                  <div className="bg-white/20 p-1 rounded-lg group-hover:bg-white/30 transition-colors">
                    <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
                  </div>
                  <span>Edit Playbook</span>
                </div>
              </Button>
            )}
          </div>
        </div>

        {/* Playbook Header Card */}
        <Card className="bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-200 shadow-lg">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <div className="bg-gradient-to-br from-sky-500 to-sky-600 p-3 sm:p-4 rounded-2xl shadow-lg flex-shrink-0 self-start sm:self-center">
                <Layers className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight break-words">
                    {playbook.playbookName}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2">
                    {isOwned && (
                      <Badge className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-sm text-xs">
                        <User className="w-3 h-3 mr-1" />
                        Owned
                      </Badge>
                    )}
                    {playbook.isCore && (
                      <Badge className="bg-gradient-to-r from-violet-500 to-violet-600 text-white shadow-sm text-xs">
                        <Shield className="w-3 h-3 mr-1" />
                        Core Playbook
                      </Badge>
                    )}
                    {playbook.playbookProductName && (
                      <Badge className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-sm text-xs">
                        <Globe className="w-3 h-3 mr-1" />
                        {playbook.playbookProductName}
                      </Badge>
                    )}
                  </div>
                </div>
                
                <p className="text-base sm:text-lg text-gray-700 mb-4 leading-relaxed">
                  {playbook.playbookDescription || 'No description provided.'}
                </p>
                
                <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-600">
                  <span className="flex items-center gap-2 font-medium">
                    <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-sky-500" />
                    {playbook.playbookHelperAgents?.length || 0} agents
                  </span>
                  <span className="flex items-center gap-2 font-medium">
                    <Wrench className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
                    {(playbook.playbookActiveTools?.length || 0) + (playbook.playbookApprovalTools?.length || 0)} tools
                  </span>
                  <span className="flex items-center gap-2 font-medium">
                    <Settings className="w-3 h-3 sm:w-4 sm:h-4 text-violet-500" />
                    <span className="hidden sm:inline">{playbook.playbookModel || 'Default'} model</span>
                    <span className="sm:hidden">{playbook.playbookModel || 'Default'}</span>
                  </span>
                  {playbook.playbookTags && playbook.playbookTags.length > 0 && (
                    <span className="flex items-center gap-2 font-medium">
                      <Tag className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500" />
                      {playbook.playbookTags.length} tags
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Configuration Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Settings className="h-5 w-5 text-sky-600" />
                Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Chat Type</label>
                <div className="bg-gray-100 px-3 py-2 rounded-lg text-sm text-gray-800 border break-words">
                  {playbook.playbookChatType || 'Standard'}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Model</label>
                <div className="bg-gray-100 px-3 py-2 rounded-lg text-sm text-gray-800 border break-words">
                  {playbook.playbookModel || 'Default'}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Product</label>
                <div className="bg-gray-100 px-3 py-2 rounded-lg text-sm text-gray-800 border break-words">
                  {playbook.playbookProductName || 'Not specified'}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Workflow Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Code className="h-5 w-5 text-emerald-600" />
                Workflow
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {playbook.playbookWorkflow ? (
                <div className="bg-gray-100 px-3 sm:px-4 py-3 rounded-lg text-sm text-gray-800 border leading-relaxed whitespace-pre-wrap overflow-x-auto">
                  {playbook.playbookWorkflow}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8 text-gray-500">
                  <Code className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm sm:text-base">No workflow defined</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Special Instructions Section */}
          {playbook.playbookSpecialInstructions && (
            <Card className="shadow-lg border-gray-200">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <CardTitle className="flex items-center gap-2 text-gray-900">
                  <FileText className="h-5 w-5 text-orange-600" />
                  Special Instructions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <div className="bg-gray-100 px-3 sm:px-4 py-3 rounded-lg text-sm text-gray-800 border leading-relaxed whitespace-pre-wrap overflow-x-auto">
                  {playbook.playbookSpecialInstructions}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Helper Agents Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Bot className="h-5 w-5 text-violet-600" />
                Helper Agents ({playbook.playbookHelperAgents?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {playbook.playbookHelperAgents && playbook.playbookHelperAgents.length > 0 ? (
                <div className="space-y-3">
                  {playbook.playbookHelperAgents.map((agent, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900 break-words">{agent.agentName}</h4>
                        <Badge variant="secondary" className="text-xs self-start sm:self-center">
                          Agent
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {agent.agentDescription || 'No description provided'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8 text-gray-500">
                  <Bot className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm sm:text-base">No helper agents configured</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tools Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Wrench className="h-5 w-5 text-emerald-600" />
                Tools ({(playbook.playbookActiveTools?.length || 0) + (playbook.playbookApprovalTools?.length || 0)})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Active Tools */}
                {playbook.playbookActiveTools && playbook.playbookActiveTools.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-emerald-500" />
                      Active Tools ({playbook.playbookActiveTools.length})
                    </h4>
                    <div className="space-y-3">
                      {playbook.playbookActiveTools.map((tool, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-emerald-50">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                            <h5 className="font-semibold text-gray-900 break-words">{tool.toolName}</h5>
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs self-start sm:self-center">
                              Active
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {tool.toolDescription || 'No description provided'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Approval Tools */}
                {playbook.playbookApprovalTools && playbook.playbookApprovalTools.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-orange-500" />
                      Approval Tools ({playbook.playbookApprovalTools.length})
                    </h4>
                    <div className="space-y-3">
                      {playbook.playbookApprovalTools.map((tool, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-orange-50">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                            <h5 className="font-semibold text-gray-900 break-words">{tool.toolName}</h5>
                            <Badge className="bg-orange-100 text-orange-700 text-xs self-start sm:self-center">
                              Approval Required
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {tool.toolDescription || 'No description provided'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!playbook.playbookActiveTools || playbook.playbookActiveTools.length === 0) &&
                 (!playbook.playbookApprovalTools || playbook.playbookApprovalTools.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <Wrench className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p>No tools configured</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Approval Notes Section */}
          {playbook.playbookApprovalNotes && (
            <Card className="shadow-lg border-gray-200">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <CardTitle className="flex items-center gap-2 text-gray-900">
                  <FileText className="h-5 w-5 text-orange-600" />
                  Approval Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="bg-gray-100 px-4 py-3 rounded-lg text-sm text-gray-800 border leading-relaxed whitespace-pre-wrap">
                  {playbook.playbookApprovalNotes}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Metadata & Access Control */}
        <div className="space-y-4 sm:space-y-6">
          {/* Tags Section */}
          {playbook.playbookTags && playbook.playbookTags.length > 0 && (
            <Card className="shadow-lg border-gray-200">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <CardTitle className="flex items-center gap-2 text-gray-900">
                  <Tag className="h-5 w-5 text-violet-600" />
                  Tags
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-wrap gap-2">
                  {playbook.playbookTags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors text-xs sm:text-sm"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Access Control Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Users className="h-5 w-5 text-sky-600" />
                Access Control
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div>
                <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 block">View Access</label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <Badge variant="outline" className="text-xs sm:text-sm self-start">
                    {playbook.accessControl?.viewAccessType || 'Owner Only'}
                  </Badge>
                  {playbook.accessControl?.viewAccessType === 'Allowed Users' && playbook.accessControl.viewAllowedUsers && (
                    <span className="text-xs text-gray-500">
                      ({playbook.accessControl.viewAllowedUsers.length} users)
                    </span>
                  )}
                </div>
              </div>
              
              <div>
                <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 block">Edit Access</label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <Badge variant="outline" className="text-xs sm:text-sm self-start">
                    {playbook.accessControl?.editAccessType || 'Owner Only'}
                  </Badge>
                  {playbook.accessControl?.editAccessType === 'Allowed Users' && playbook.accessControl.editAllowedUsers && (
                    <span className="text-xs text-gray-500">
                      ({playbook.accessControl.editAllowedUsers.length} users)
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Metadata Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Database className="h-5 w-5 text-yellow-600" />
                Metadata
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-3">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-xs sm:text-sm">
                <span className="text-gray-600 font-medium">Playbook ID</span>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono break-all">
                    {playbook.playbookId}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyToClipboard(playbook.playbookId || '', 'Playbook ID')}
                    className="h-6 w-6 p-0 hover:bg-gray-100 flex-shrink-0"
                  >
                    {copiedField === 'Playbook ID' ? (
                      <CheckCircle className="h-3 w-3 text-green-600" />
                    ) : (
                      <Copy className="h-3 w-3 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <span className="text-gray-600 font-medium">Owner</span>
                <span className="text-gray-900 font-medium break-all text-right">
                  {isOwned ? (user?.email || 'You') : (ownerEmail || playbook.ownerId)}
                </span>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <span className="text-gray-600 font-medium">Type</span>
                <span className="text-gray-900 font-medium text-right">
                  {playbook.isCore ? 'Core Playbook' : 'Custom Playbook'}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Trigger Modal */}
      <TriggerModal
        isOpen={triggerModalOpen}
        onClose={() => setTriggerModalOpen(false)}
        playbookId={playbook.playbookId}
        playbookName={playbook.playbookName}
        onSubmit={handleTriggerSubmit}
      />
    </div>
  );
};

export default ViewPlaybookPage; 